/**
 * An organization chart for one firm: the firm at the top, every person the
 * CRM holds under it beneath, joined by connector lines.
 *
 * Owner, 21 Oct: "When a brokerage is chosen in CRM it should include an org
 * chart showing the brokerage at the top and all the brokers who work for it
 * that are saved in CRM."
 *
 * 26 Oct (CY-24): where the firm's people sit in more than one office, the
 * chart is firm, office, people, each office saying how many people it holds
 * and opening or closing on its own. With one office on record (or none) it
 * stays the single tier: no office node is invented. The person a profile is
 * about is marked, and their office is the one that opens.
 *
 * Purely presentational: it draws the people `GET /crm/organizations/members`
 * returned and nothing else. The CRM records no reports-to line, so there is no
 * hierarchy among the people to invent; within the firm or an office they sit
 * in one tier, most senior title first (see `orgChartLayout`). An empty firm
 * is the firm node alone.
 */
import { useId, useLayoutEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { Building2, ChevronDown, MapPin } from 'lucide-react'

import type { CrmOrganizationMembers } from '@/services/crm'
import {
  chunkRows,
  columnsForWidth,
  displayName,
  firmKindLabel,
  groupByOffice,
  orderBySeniority,
  tierGeometry,
  type OfficeGroup,
} from './orgChartLayout'

type Member = CrmOrganizationMembers['people'][number]
type FirmRecord = CrmOrganizationMembers['records'][number]

/** Connector styling: one neutral hairline, no new colors. */
const LINE = 'absolute bg-stone-700'
/** Vertical room above each row for its bus (half) and the drops (half). */
const BUS_Y = 10
/** Where the office spine runs, from the chart's left edge, in px. */
const SPINE_X = 12
/** How far an office (and its people) sits in from the chart's left edge, in px. */
const OFFICE_INDENT = 24
/** Room above each office node, in px. */
const OFFICE_GAP = 8
/** Where an office's branch meets it: the middle of its first line, in px from the top of its slot. */
const OFFICE_TICK_Y = OFFICE_GAP + 13

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

function PersonNode({
  person,
  self,
  showOffice,
  onOpenContact,
}: {
  person: Member
  /** The person the surrounding profile is about: marked, and not a link to itself. */
  self: boolean
  /** Whether the card says the office; under an office node it would only repeat it. */
  showOffice: boolean
  onOpenContact?: (contactId: string) => void
}) {
  const descriptionId = useId()
  const label = displayName(person)
  const deals = new Set(person.deals.map((d) => d.dealId)).size
  const office = showOffice ? person.office || null : null
  const description = [
    person.title,
    person.email,
    office,
    deals ? plural(deals, 'linked deal', 'linked deals') : null,
  ]
    .filter(Boolean)
    .join(', ')
  const card = clsx(
    'group flex h-full w-full min-w-0 flex-col items-start gap-0.5 rounded border px-2 py-1.5 text-left transition-colors',
    self
      ? 'border-amber-400/60 bg-amber-400/[0.08]'
      : 'border-stone-800/80 bg-black/20 hover:border-amber-400/40 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
  )
  const body = (
    <>
      <span
        className={clsx(
          'block w-full break-words text-[11px] font-medium leading-tight',
          self ? 'text-amber-100' : 'text-stone-100 group-hover:text-amber-200',
        )}
      >
        {label}
      </span>
      {person.title ? (
        <span className="line-clamp-2 w-full break-words text-[10px] leading-tight text-stone-400" title={person.title}>
          {person.title}
        </span>
      ) : null}
      {person.email ? (
        <span className="block w-full truncate text-[10px] text-stone-500" title={person.email}>
          {person.email}
        </span>
      ) : null}
      {office ? (
        <span
          className="block w-full truncate text-[10px] text-stone-500"
          title={person.officeBasis ? `${office} (${person.officeBasis})` : office}
        >
          {office}
        </span>
      ) : null}
      {deals || self ? (
        <span className="flex flex-wrap gap-x-2 font-mono text-[9px] uppercase tracking-[0.12em] text-stone-500">
          {self ? <span className="text-amber-300/90">This contact</span> : null}
          {deals ? <span>{plural(deals, 'deal', 'deals')}</span> : null}
        </span>
      ) : null}
    </>
  )
  if (self) {
    // Opening the profile already open would do nothing; the card is a fact.
    return (
      <div aria-current="true" className={card}>
        {body}
      </div>
    )
  }
  return (
    <>
      {onOpenContact ? (
        <button
          type="button"
          onClick={() => onOpenContact(person.id)}
          aria-label={label}
          aria-describedby={description ? descriptionId : undefined}
          className={card}
        >
          {body}
        </button>
      ) : (
        // Same fallback the list uses where the screen supplies no opener.
        <a
          href={`/crm?focus=${encodeURIComponent(person.id)}&open=1`}
          aria-label={label}
          aria-describedby={description ? descriptionId : undefined}
          className={card}
        >
          {body}
        </a>
      )}
      {description ? (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      ) : null}
    </>
  )
}

/**
 * People in rows under one parent (the firm, or an office): one trunk down the
 * centre, a bus per row, a drop to each card.
 */
function PeopleTiers({
  people,
  columns,
  focusId,
  showOffice,
  onOpenContact,
}: {
  people: readonly Member[]
  columns: number
  focusId?: string | null
  showOffice: boolean
  onOpenContact?: (contactId: string) => void
}) {
  const rows = chunkRows(people, columns)
  return (
    <>
      {rows.map((row, index) => {
        const isLast = index === rows.length - 1
        const geometry = tierGeometry(row.length, columns)
        return (
          <div key={row[0].id} className="relative pt-5" data-tier={index + 1}>
            {/* The trunk: the centre of the gap between the middle columns. It
                runs the whole row, and stops at the bus on the last one. */}
            <span
              aria-hidden
              className={`${LINE} left-1/2 top-0 w-px`}
              style={{ height: isLast ? BUS_Y + 1 : '100%' }}
            />
            {geometry.hasBus ? (
              <span
                aria-hidden
                className={`${LINE} h-px`}
                style={{ top: BUS_Y, left: geometry.busLeft, right: geometry.busRight }}
              />
            ) : null}
            <ul
              className={`grid gap-x-3 ${columns === 1 ? 'mx-auto max-w-[16rem]' : ''}`}
              style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
            >
              {row.map((person) => (
                <li key={person.id} className="relative min-w-0">
                  <span aria-hidden className={`${LINE} -top-2.5 left-1/2 h-2.5 w-px`} />
                  <PersonNode
                    person={person}
                    self={Boolean(focusId) && person.id === focusId}
                    showOffice={showOffice}
                    onOpenContact={onOpenContact}
                  />
                </li>
              ))}
            </ul>
          </div>
        )
      })}
    </>
  )
}

const groupId = (group: OfficeGroup<Member>) => group.key ?? ''

/** One office: its node on the spine, and its people beneath it when open. */
function OfficeBranch({
  group,
  isLast,
  open,
  onToggle,
  columns,
  focusId,
  onOpenContact,
}: {
  group: OfficeGroup<Member>
  isLast: boolean
  open: boolean
  onToggle: () => void
  columns: number
  focusId?: string | null
  onOpenContact?: (contactId: string) => void
}) {
  const listId = useId()
  const count = plural(group.people.length, 'person', 'people')
  const recorded = group.key !== null
  return (
    <li
      className="relative"
      data-office={group.label}
      style={{ paddingLeft: OFFICE_INDENT, paddingTop: OFFICE_GAP }}
    >
      {/* The spine down the left, carried on to the next office; the last
          office's piece stops at its own branch. */}
      <span
        aria-hidden
        className={`${LINE} top-0 w-px`}
        style={{ left: SPINE_X, height: isLast ? OFFICE_TICK_Y + 1 : '100%' }}
      />
      <span
        aria-hidden
        className={`${LINE} h-px`}
        style={{ left: SPINE_X, top: OFFICE_TICK_Y, width: OFFICE_INDENT - SPINE_X }}
      />
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${group.label}, ${count}`}
        onClick={onToggle}
        className="flex w-full min-w-0 items-start gap-1.5 rounded border border-stone-700/80 bg-black/30 px-2 py-1.5 text-left transition-colors hover:border-amber-400/40 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
      >
        <MapPin
          size={11}
          className={clsx('mt-px shrink-0', recorded ? 'text-amber-400/80' : 'text-stone-600')}
          aria-hidden
        />
        {/* Name over count, as the firm node reads, so a narrow panel wraps
            the words rather than pushing the count past the edge. */}
        <span className="min-w-0 flex-1">
          <span
            className={clsx(
              'block break-words text-[11px] font-medium leading-tight',
              recorded ? 'text-stone-200' : 'text-stone-400',
            )}
          >
            {group.label}
          </span>
          <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.12em] text-stone-500">{count}</span>
        </span>
        <ChevronDown
          size={11}
          className={clsx('mt-px shrink-0 text-stone-500 transition-transform', !open && '-rotate-90')}
          aria-hidden
        />
      </button>
      <div id={listId} hidden={!open}>
        {open ? (
          <PeopleTiers
            people={group.people}
            columns={group.people.length === 1 ? 1 : columns}
            focusId={focusId}
            showOffice={false}
            onOpenContact={onOpenContact}
          />
        ) : null}
      </div>
    </li>
  )
}

export default function OrganizationChart({
  name,
  records = [],
  people,
  focusId,
  onOpenContact,
}: {
  /** The firm at the top of the chart. */
  name: string
  /** The firm's own CRM records, for the kind label. */
  records?: readonly FirmRecord[]
  /** Everyone the CRM holds under the firm. */
  people: readonly Member[]
  /** The person the surrounding profile is about: marked, and their office opens. */
  focusId?: string | null
  onOpenContact?: (contactId: string) => void
}) {
  const frame = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | null>(null)
  // Offices the reader opened or closed; the rest follow the default below.
  const [toggled, setToggled] = useState<Record<string, boolean>>({})

  // Rows hold as many cards as the width allows (2 or 4). Measured on mount and
  // on resize; a zero width (hidden, or no layout engine) leaves the last value.
  useLayoutEffect(() => {
    const el = frame.current
    if (!el) return undefined
    const measure = () => {
      const measured = el.getBoundingClientRect().width
      if (measured > 0) setWidth(measured)
    }
    measure()
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const ordered = orderBySeniority(people)
  const offices = groupByOffice(ordered)
  // Under an office the cards sit in from the spine, so they have less room.
  const measured = columnsForWidth(width === null ? null : offices ? width - OFFICE_INDENT : width)
  // One person hangs straight under the firm, centred; more share the grid.
  const columns = !offices && ordered.length === 1 ? 1 : measured
  // With a person to show, their office is the one open; otherwise all are.
  const focusOffice = focusId && offices ? offices.find((g) => g.people.some((p) => p.id === focusId)) : undefined
  const isOpen = (group: OfficeGroup<Member>) =>
    toggled[groupId(group)] ?? (focusOffice ? group === focusOffice : true)
  const recordedOffices = offices ? offices.filter((g) => g.key !== null).length : 0

  return (
    <div
      ref={frame}
      role="group"
      aria-label={`Organization chart for ${name}`}
      data-columns={columns}
      className="org-chart"
    >
      <div className={clsx('flex', offices ? 'justify-start' : 'justify-center')}>
        <div className="flex w-fit max-w-full items-start gap-1.5 rounded border border-amber-400/40 bg-amber-400/[0.06] px-3 py-1.5">
          <Building2 size={12} className="mt-0.5 shrink-0 text-amber-400" aria-hidden />
          <div className="min-w-0">
            <div className="break-words text-[12px] font-medium leading-tight text-stone-100">{name}</div>
            <div className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-stone-500">
              {firmKindLabel(records)} · {plural(ordered.length, 'person', 'people')}
              {offices ? ` · ${plural(recordedOffices, 'office', 'offices')}` : ''}
            </div>
          </div>
        </div>
      </div>

      {ordered.length === 0 ? (
        <p className="mt-2 text-center text-stone-500">No people are saved under this organization in the CRM yet.</p>
      ) : offices ? (
        <ul aria-label={`Offices of ${name}`}>
          {offices.map((group, index) => (
            <OfficeBranch
              key={groupId(group)}
              group={group}
              isLast={index === offices.length - 1}
              open={isOpen(group)}
              onToggle={() => setToggled((was) => ({ ...was, [groupId(group)]: !isOpen(group) }))}
              columns={measured}
              focusId={focusId}
              onOpenContact={onOpenContact}
            />
          ))}
        </ul>
      ) : (
        <PeopleTiers
          people={ordered}
          columns={columns}
          focusId={focusId}
          showOffice
          onOpenContact={onOpenContact}
        />
      )}
    </div>
  )
}
